<script setup lang="ts">
import {
  ref,
  computed,
  reactive,
  watch,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  defineComponent,
  shallowRef,
  useSlots,
  type Component,
} from 'vue';
import { debounce } from 'lodash-es';
import { required as requiredRule } from '@regle/rules';
import {
  useItpFormControl,
  useItpFormField,
  useItpFormValidation,
} from './useItpForm';
import {
  useItpSelectProvider,
  normalizeOptionsInput,
  isEmptyValue,
} from './useItpSelectProvider';
import vSelect from 'vue-select';
import { createPopper } from '@popperjs/core';
import 'vue-select/dist/vue-select.css';
import { formMessages } from '../../messages';
import { errorDetail } from '../loading/errorDetail';

type Mode = 'native' | 'rich';

const props = defineProps<{
  modelValue?: any;
  name: string;
  mode?: Mode;

  multiple?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  nullOption?: boolean;

  valueField?: string;
  textField?: string;
  textFieldSeparator?: string;
  disabledField?: string;

  options?: any[] | Record<string, any> | ((q: string) => Promise<any[]>);
  initialOptions?: any[];
  dataSource?:
    | { name: string; parameters?: Record<string, any> }
    | (() => { name: string; parameters?: Record<string, any> });
  prefetch?: boolean;

  clearable?: boolean;
  bindObject?: boolean;

  optionTemplate?: string;
  useOptionTemplateForSelectedOption?: boolean;

  request?: (name: string, parameters: Record<string, any>) => Promise<any>;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: any): void;
  (event: 'change', value: any): void;
  (event: 'input', value: any): void;
  (event: 'optionSelected', value: any): void;
}>();

const { isDisabled, isReadonly } = useItpFormControl(props);

function toValidationValue(val: any): any {
  if (val === null || val === undefined) {
    return val;
  }
  if (Array.isArray(val)) {
    return val.length > 0 ? val.map(toValidationValue) : val;
  }
  if (typeof val === 'object') {
    if (isEmptyValue(val)) {
      return null;
    }
    const vf = props.valueField ?? 'value';
    return val[vf] ?? '__has_value__';
  }
  return val;
}

const formFieldProps = reactive({
  name: computed(() => props.name),
  modelValue: computed(() => toValidationValue(props.modelValue)),
  required: computed(() => props.required),
});

const { touch } = useItpFormField(formFieldProps, (isRequired) =>
  isRequired ? { required: requiredRule } : {},
);

const { isInvalid } = useItpFormValidation(props.name);

const instance = getCurrentInstance();
const $app = (instance?.proxy as any)?.$app ?? null;
const connections = $app?.connections;
const isRich = computed(() => props.mode !== 'native');

const slots = useSlots();
const RESERVED_SELECT_SLOTS = [
  'option',
  'selected-option',
  'no-options',
  'list-header',
];
const passthroughSlotNames = computed(() =>
  Object.keys(slots).filter((name) => !RESERVED_SELECT_SLOTS.includes(name)),
);

const effectiveValueField = computed(() => props.valueField ?? 'value');
const effectiveTextField = computed(() => props.textField ?? 'text');

const knownOptions = ref<Map<any, any>>(new Map());

const provider = useItpSelectProvider({
  options: props.options as any,
  initialOptions: props.initialOptions,
  dataSource: (() => props.dataSource) as any,
  prefetch: props.prefetch,
  valueField: props.valueField,
  textField: props.textField,
  disabledField: props.disabledField,
  request:
    props.request ??
    (connections
      ? (name: string, parameters: Record<string, any>) =>
          connections.request(name, { parameters })
      : undefined),
});

function resolveField(obj: any, path: string): any {
  if (obj == null || typeof obj !== 'object') {
    return undefined;
  }
  if (!path.includes('.')) {
    return obj[path];
  }
  return path.split('.').reduce((acc: any, key: string) => acc?.[key], obj);
}

function getObjectLabel(option: any): string {
  const textFields = effectiveTextField.value;
  const fields = textFields
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);
  const label = fields
    .map((field) => resolveField(option, field))
    .filter((val) => val != null && String(val) !== '')
    .join(props.textFieldSeparator ?? ' ');
  if (label) {
    return label;
  }

  const fallbacks = [
    'name',
    'label',
    'text',
    'title',
    effectiveValueField.value,
  ];
  for (const field of fallbacks) {
    if (fields.includes(field)) {
      continue;
    }
    const val = option[field];
    if (val != null && String(val) !== '') {
      return String(val);
    }
  }
  return '';
}

function getOptionLabel(option: any): string {
  if (option === null || option === undefined) {
    return '';
  }
  if (typeof option !== 'object') {
    const known = knownOptionFor(option);
    if (known) {
      return getObjectLabel(known);
    }
    if (provider.isAsync() && props.valueField) {
      return '';
    }
    return String(option);
  }
  return getObjectLabel(option);
}

function getOptionKey(option: any): any {
  if (option === null || option === undefined) {
    return String(option);
  }
  if (typeof option !== 'object') {
    return option;
  }
  return option[effectiveValueField.value] ?? JSON.stringify(option);
}

function isOptionSelected(option: any): boolean {
  if (
    !props.multiple ||
    !Array.isArray(props.modelValue) ||
    props.modelValue.length === 0
  ) {
    return false;
  }
  const optionKey = getOptionKey(option);
  if (optionKey === null || optionKey === undefined) {
    return false;
  }
  return props.modelValue.some(
    (selected: any) => getOptionKey(selected) === optionKey,
  );
}

function isOptionSelectable(option: any): boolean {
  return !isOptionSelected(option);
}

function withPopper(
  dropdownList: HTMLElement,
  component: any,
  { width }: { width: string },
) {
  dropdownList.style.width = width;
  const referenceElement = component.$refs.toggle || component.$el;

  const popper = createPopper(referenceElement, dropdownList, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, -1] },
      },
    ],
  });

  return () => popper.destroy();
}

const nativeOptions = computed(() => {
  const initialOpts = props.initialOptions;
  const normalized = normalizeOptionsInput(props.options);
  const list =
    initialOpts && initialOpts.length > 0
      ? initialOpts
      : Array.isArray(normalized)
        ? normalized
        : [];
  return list
    .filter((item) => !isEmptyValue(item))
    .map((item) => {
      const n = provider.toNormalized(item);
      return {
        value: n.value,
        text: n.label,
        disabled: n.disabled || false,
        raw: n.raw,
      };
    });
});

function getItemKey(item: any): any {
  if (item === null || item === undefined) {
    return item;
  }
  if (typeof item !== 'object') {
    return item;
  }
  return item[effectiveValueField.value];
}

const nativeValue = computed({
  get() {
    if (props.bindObject) {
      if (props.multiple) {
        if (Array.isArray(props.modelValue)) {
          return props.modelValue.map((obj) => getItemKey(obj));
        }
        return [];
      }
      return getItemKey(props.modelValue) ?? null;
    }
    return props.modelValue ?? (props.multiple ? [] : null);
  },
  set(newVal) {
    let output: any = newVal;
    let rawOption: any = null;
    if (props.bindObject) {
      if (props.multiple) {
        const valueSet = new Set(Array.isArray(newVal) ? newVal : []);
        const matched = nativeOptions.value.filter((opt) =>
          valueSet.has(opt.value),
        );
        output = matched.map((opt) => opt.raw);
        rawOption = output;
      } else {
        const found = nativeOptions.value.find((opt) => opt.value === newVal);
        output = found?.raw ?? null;
        rawOption = found?.raw ?? null;
      }
    } else {
      const found = nativeOptions.value.find((opt) => opt.value === newVal);
      rawOption = found?.raw ?? null;
    }
    emit('update:modelValue', output);
    emit('change', output);
    emit('input', output);
    if (output != null) {
      emit('optionSelected', { value: output, option: rawOption });
    }
  },
});

function reduceOption(option: any): any {
  if (option === null || option === undefined) {
    return option;
  }
  if (typeof option !== 'object') {
    return option;
  }
  if (props.bindObject) {
    return option;
  }
  return option[effectiveValueField.value];
}

function rememberOptions(options: any[]) {
  for (const option of options) {
    if (option && typeof option === 'object') {
      knownOptions.value.set(reduceOption(option), option);
    }
  }
}

function knownOptionFor(value: any): any {
  const key = value && typeof value === 'object' ? reduceOption(value) : value;
  return key == null ? undefined : knownOptions.value.get(key);
}

function selectedOptionObject(value: any): any {
  if (value && typeof value === 'object') {
    return value;
  }
  return knownOptionFor(value) ?? null;
}

const richOptions = ref<any[]>(
  isRich.value
    ? provider.loadInitial().map((inputItem) => inputItem.raw ?? inputItem)
    : [],
);
rememberOptions(richOptions.value);

const richLoading = ref(false);
const searchError = ref<unknown>(null);
const searchErrorText = computed(
  () => `${formMessages.searchFailed} ${errorDetail(searchError.value)}`,
);
let searchSeq = 0;

const richSearchText = ref('');

function mapKeydown(
  map: Record<number, (event: KeyboardEvent) => unknown>,
  vs: { open: boolean; search: string },
) {
  return {
    ...map,
    27: (event: KeyboardEvent) => {
      if (vs.open || vs.search) {
        event.preventDefault();
      }
      return map[27](event);
    },
  };
}

const richClass = computed(() => ({
  'size-sm': props.size === 'sm',
  'size-lg': props.size === 'lg',
  'is-invalid': isInvalid.value,
}));

const selectedOptions = computed<any[]>(() => {
  const value = props.modelValue;
  if (isEmptyValue(value)) {
    return [];
  }
  return (Array.isArray(value) ? value : [value])
    .map((inputValue) =>
      inputValue && typeof inputValue === 'object'
        ? inputValue
        : knownOptionFor(inputValue),
    )
    .filter(Boolean);
});

const vselectOptions = computed<any[]>(() => {
  const options = richOptions.value.filter((opt) => !isEmptyValue(opt));
  const searching = richSearchText.value.trim().length > 0;
  if (!selectedOptions.value.length || (searching && !props.multiple)) {
    return options;
  }
  const present = new Set(options.map((opt) => reduceOption(opt)));
  const missing = selectedOptions.value.filter(
    (opt) => !present.has(reduceOption(opt)),
  );
  return missing.length ? [...missing, ...options] : options;
});

const richSelected = computed({
  get() {
    const val = props.modelValue;
    if (isEmptyValue(val)) {
      return props.multiple ? [] : null;
    }
    return val;
  },
  set(newVal) {
    let rawOption: any = newVal;
    if (newVal != null && typeof newVal !== 'object') {
      rawOption =
        richOptions.value.find((opt) => reduceOption(opt) === newVal) ?? newVal;
    }

    rememberOptions(
      (Array.isArray(newVal) ? newVal : [rawOption]).map((inputValue) =>
        inputValue != null && typeof inputValue === 'object'
          ? inputValue
          : richOptions.value.find((opt) => reduceOption(opt) === inputValue),
      ),
    );

    emit('update:modelValue', newVal);
    emit('change', newVal);
    emit('input', newVal);
    if (newVal != null) {
      emit('optionSelected', { value: newVal, option: rawOption });
    }
  },
});

async function fetchRichOptions(
  query: string,
  loading?: (isLoading: boolean) => void,
) {
  const seq = ++searchSeq;
  loading?.(true);
  richLoading.value = true;

  try {
    const normalized = await provider.search(query);
    if (seq !== searchSeq) {
      return;
    }
    searchError.value = null;
    richOptions.value = normalized.map((item) => item.raw ?? item);
    rememberOptions(richOptions.value);
  } catch (error) {
    console.error('[ItpFormSelect] Search error:', error);
    if (seq === searchSeq) {
      searchError.value = error;
      richOptions.value = [];
    }
  } finally {
    if (seq === searchSeq) {
      loading?.(false);
      richLoading.value = false;
    }
  }
}

const SEARCH_DELAY_MS = 500;
const searchWhenTypingPauses = debounce(fetchRichOptions, SEARCH_DELAY_MS);
let dropdownOpen = false;

function onRichSearch(query: string, loading: (b: boolean) => void) {
  if (!provider.isAsync()) {
    return;
  }
  richSearchText.value = query ?? '';
  if (!dropdownOpen) {
    return;
  }
  const trimmed = richSearchText.value.trim();
  void searchWhenTypingPauses(trimmed || '*', loading);
}

function onRichOpen() {
  dropdownOpen = true;
  if (!provider.isAsync() || richSearchText.value.trim()) {
    return;
  }
  void fetchRichOptions('*');
}

function onRichClose() {
  dropdownOpen = false;
  searchWhenTypingPauses.cancel();
  richSearchText.value = '';
  touch();
}

onBeforeUnmount(() => searchWhenTypingPauses.cancel());

onMounted(() => {
  if (isRich.value) {
    if (props.prefetch && provider.isAsync()) {
      const seq = ++searchSeq;
      richLoading.value = true;
      provider
        .search('')
        .then((normalized) => {
          if (seq === searchSeq) {
            searchError.value = null;
            richOptions.value = normalized.map((n) => n.raw ?? n);
            rememberOptions(richOptions.value);
          }
        })
        .catch((error) => {
          console.error('[ItpFormSelect] Prefetch error:', error);
          if (seq === searchSeq) {
            searchError.value = error;
          }
        })
        .finally(() => {
          if (seq === searchSeq) {
            richLoading.value = false;
          }
        });
    }
  }
});

watch(
  () => props.options,
  (newOptions) => {
    if (isRich.value) {
      const normalized = normalizeOptionsInput(newOptions);
      if (Array.isArray(normalized)) {
        richOptions.value = normalized;
      }
    }
  },
  { immediate: true },
);

const compiledOptionComponent = shallowRef<Component | null>(null);

watch(
  () => props.optionTemplate,
  (tmpl) => {
    if (tmpl) {
      compiledOptionComponent.value = defineComponent({
        name: 'CompiledOptionTemplate',
        props: ['option'],
        template: tmpl,
      });
    } else {
      compiledOptionComponent.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <select
    v-if="!isRich"
    :id="name"
    v-model="nativeValue"
    :name="name"
    :multiple="multiple"
    :disabled="isDisabled || isReadonly"
    class="form-select"
    :class="{
      'form-select-sm': size === 'sm',
      'form-select-lg': size === 'lg',
      'is-invalid': isInvalid,
    }"
    @change="touch"
  >
    <option v-if="nullOption && !multiple" :value="null" />
    <option v-else-if="!multiple && nativeValue == null" :value="null" disabled>
      {{ placeholder || '---' }}
    </option>
    <option
      v-for="opt in nativeOptions"
      :key="opt.value ?? String(opt.text)"
      :value="opt.value"
      :disabled="opt.disabled"
    >
      {{ opt.text }}
    </option>
  </select>

  <vSelect
    v-else
    :id="name"
    v-model="richSelected"
    :class="richClass"
    :name="name"
    :options="vselectOptions"
    :multiple="multiple"
    :clearable="clearable"
    :searchable="true"
    :placeholder="placeholder"
    :loading="richLoading"
    :label="effectiveTextField"
    :reduce="reduceOption"
    :get-option-label="getOptionLabel"
    :get-option-key="getOptionKey"
    :selectable="isOptionSelectable"
    :dropdown-should-open="(vs: any) => !vs.noDrop && vs.open"
    :disabled="isDisabled || isReadonly"
    :append-to-body="true"
    :calculate-position="withPopper"
    :filterable="!provider.isAsync()"
    :map-keydown="mapKeydown"
    @search="onRichSearch"
    @open="onRichOpen"
    @close="onRichClose"
  >
    <template #option="slotProps">
      <slot name="option" v-bind="slotProps">
        <component
          :is="compiledOptionComponent"
          v-if="compiledOptionComponent"
          :option="slotProps"
        />
        <template v-else>{{ getOptionLabel(slotProps) }}</template>
      </slot>
    </template>

    <template #selected-option="slotProps">
      <slot name="selected-option" v-bind="slotProps">
        <component
          :is="compiledOptionComponent"
          v-if="
            compiledOptionComponent &&
            useOptionTemplateForSelectedOption &&
            selectedOptionObject(slotProps)
          "
          :option="selectedOptionObject(slotProps)"
        />
        <span
          v-else
          class="itp-select__selected-text"
          :title="getOptionLabel(slotProps)"
          >{{ getOptionLabel(slotProps) }}</span
        >
      </slot>
    </template>

    <template #list-header="slotProps">
      <li
        v-if="searchError && vselectOptions.length"
        class="vs__no-options text-danger"
        role="alert"
      >
        {{ searchErrorText }}
      </li>
      <slot name="list-header" v-bind="slotProps ?? {}" />
    </template>

    <template #no-options>
      <slot name="no-options">
        <span v-if="searchError" class="text-danger" role="alert">{{
          searchErrorText
        }}</span>
        <template v-else>{{ formMessages.noResults }}</template>
      </slot>
    </template>

    <template v-for="name in passthroughSlotNames" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
  </vSelect>
</template>
